'use client'

import React from 'react'
import { Trans, useTranslation } from 'react-i18next'
import type { ReleaseStatus } from '../../lib/latest-release'
import { DownloadButton } from '../../components/landing/download-button'
import { StoreButton } from '../../components/landing/store-button'
import { SiteContainer } from '../../components/site/section'
import { useSectionViewed } from '../../lib/analytics/use-section-viewed'
import { trackSiteLinkClicked } from '@/lib/analytics'

/**
 * 文末下載。左邊只放標語，右邊放下載——標語兩層（襯線大標是喊出來的那句，
 * 底下產品定位那句退回內文字級），整區靠左，跟頁面其他段落同一條軸線。
 *
 * 按鈕上只留平台名：標籤已經寫了「桌面版／行動版」，「下載 macOS 版本」那種
 * 長標籤會把按鈕撐成一塊肥磚。高度仍保 44px 觸控下限。
 */

export interface DownloadCtaProps {
  release: ReleaseStatus
  fallbackUrl?: string
  windowsRelease?: ReleaseStatus
  windowsFallbackUrl?: string
  /** false 時 Windows 那顆換成「準備中」佔位，不是下載連結。 */
  windowsDownloadEnabled?: boolean
}

const COMPACT_BUTTON = 'min-h-11 gap-[7px] px-4 text-base'

export function DownloadCta({
  release,
  fallbackUrl,
  windowsRelease = null,
  windowsFallbackUrl,
  windowsDownloadEnabled = true,
}: DownloadCtaProps) {
  const { t } = useTranslation(['web'])
  const viewedRef = useSectionViewed('download')

  return (
    <section
      ref={viewedRef}
      id="download"
      className="bg-foreground py-(--section-space) text-background"
    >
      <SiteContainer className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-(--gap-xl) max-[960px]:grid-cols-1">
        <div>
          <p className="mb-(--gap-sm) font-mono text-xs tracking-[0.28em] text-background/58 uppercase">
            {t('web:dictationPage.download.eyebrow')}
          </p>
          <h2 className="font-display text-(length:--fs-marketing-h2) leading-[1.22] font-semibold tracking-[0.005em]">
            <Trans
              t={t}
              i18nKey="web:dictationPage.download.title"
              components={{ ph: <span className="inline-block" /> }}
            />
          </h2>
          <p className="mt-3.5 text-lg leading-[1.6] text-background/76">
            {t('web:dictationPage.download.sub')}
          </p>
          <p className="mt-(--gap-lg) font-mono text-(length:--fs-meta) text-background/58">
            <Trans
              t={t}
              i18nKey="web:dictationPage.download.meta"
              components={{
                faq: (
                  <a
                    href="#faq"
                    onClick={() => trackSiteLinkClicked('download', '#faq')}
                    className="border-b border-background/45 text-background transition-colors hover:border-background"
                  />
                ),
              }}
            />
          </p>
        </div>
        <div className="border-l border-inv-line pl-(--gap-xl) max-[960px]:border-t max-[960px]:border-l-0 max-[960px]:pt-(--gap-lg) max-[960px]:pl-0">
          <div className="flex flex-col items-start gap-(--gap-md)">
            <div className="flex flex-col items-start gap-(--gap-sm)">
              <p className="font-mono text-xs tracking-[0.2em] text-background/58 uppercase">
                {t('web:dictationPage.download.desktopLabel')}
              </p>
              <div className="inline-flex flex-wrap justify-start gap-(--gap-sm)">
                <DownloadButton
                  release={release}
                  fallbackUrl={fallbackUrl}
                  variant="onlight"
                  location="download"
                  className={COMPACT_BUTTON}
                />
                <DownloadButton
                  release={windowsRelease}
                  fallbackUrl={windowsFallbackUrl}
                  platform="windows"
                  variant="ondark"
                  location="download"
                  className={COMPACT_BUTTON}
                  comingSoon={!windowsDownloadEnabled}
                />
              </div>
            </div>
            <div className="flex flex-col items-start gap-(--gap-sm) border-t border-inv-line pt-(--gap-md)">
              <p className="font-mono text-xs tracking-[0.2em] text-background/58 uppercase">
                {t('web:dictationPage.download.mobileLabel')}
              </p>
              <div className="inline-flex flex-wrap justify-start gap-(--gap-sm)">
                <StoreButton
                  platform="ios"
                  variant="ondark"
                  location="download"
                  className={COMPACT_BUTTON}
                />
                <StoreButton
                  platform="android"
                  variant="ondark"
                  location="download"
                  className={COMPACT_BUTTON}
                />
              </div>
            </div>
          </div>
        </div>
      </SiteContainer>
    </section>
  )
}
